import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';

/**
 * Live version check — forces already-open WEB tabs to reload after a deploy.
 *
 * The mobile app ships two ways: the native Capacitor binary (iOS/Android) and
 * a webapp served from S3/CloudFront. This check is ONLY relevant for the
 * webapp: the native binary bundles its assets locally and is updated through
 * the app stores, so reloading on a `version.json` change would be wrong there.
 * `start()` is therefore a no-op on native platforms.
 *
 * Each web deploy publishes a `version.json` ({ "commit": "<sha>" }) next to
 * index.html with `Cache-Control: no-store`. We read it as a baseline at
 * startup, then poll (and re-check when the tab regains focus); when the
 * published commit changes, we reload once to pull the fresh bundles.
 */
@Injectable({ providedIn: 'root' })
export class VersionCheckService {
  /** How often to re-check the deployed version. */
  private static readonly POLL_INTERVAL_MS = 2 * 60 * 1000;
  /** Guards against reload loops if the fresh index.html is still stale at the edge. */
  private static readonly RELOAD_FLAG = 'fsm_version_reload';

  private baselineCommit: string | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;

  /** Called once at startup via APP_INITIALIZER. No-op on native. Never throws. */
  async start(): Promise<void> {
    // Native builds are updated via the app stores, not via version.json.
    if (Capacitor.isNativePlatform()) return;

    this.baselineCommit = await this.fetchCommit();
    if (this.baselineCommit) {
      sessionStorage.removeItem(VersionCheckService.RELOAD_FLAG);
    }

    this.timer = setInterval(
      () => void this.check(),
      VersionCheckService.POLL_INTERVAL_MS,
    );
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') void this.check();
    });
  }

  private async check(): Promise<void> {
    const current = await this.fetchCommit();
    if (!current || !this.baselineCommit) return;
    if (current === this.baselineCommit) return;

    if (sessionStorage.getItem(VersionCheckService.RELOAD_FLAG)) return;
    sessionStorage.setItem(VersionCheckService.RELOAD_FLAG, '1');
    if (this.timer) clearInterval(this.timer);
    window.location.reload();
  }

  /** Reads version.json bypassing all caches. Returns null on any failure. */
  private async fetchCommit(): Promise<string | null> {
    try {
      const res = await fetch(`version.json?t=${Date.now()}`, {
        cache: 'no-store',
      });
      if (!res.ok) return null;
      const data = (await res.json()) as { commit?: string };
      return data.commit ?? null;
    } catch {
      return null;
    }
  }
}
